import { axiosInstanceWebsite } from "@/services/instance";
import type { CompanyData, FixedCost } from "../components/dashboard/company/types";
import type { DateRange, Funnel, MetaCampaign } from "../components/dashboard/funnels/types";
import type { ChatConversation, ChatConversationDetail, ChatRequest, ChatResponse } from "../components/ai-chat/types";
import type { FunnelDetailData, Period } from "../components/funnel-detail/types";
import { formatDateForApi } from "../components/funnel-detail/periods";

export type FinanceStatusResponse = {
  backend_available?: boolean;
  finance_enabled?: boolean;
  finance_activated_at?: string | null;
  meta_connected?: boolean;
  tiktok_connected?: boolean;
  google_ads_connected?: boolean;
  dropi_connected?: boolean;
  selected_google_ads_customer_id?: string | null;
};

export type FinanceMetaConnectionResponse = {
  connected: boolean;
  status: string;
  business_id?: string | null;
  business_name?: string | null;
  selected_ad_account_id?: string | null;
  selected_ad_account_name?: string | null;
  selected_ad_account_ids?: string[] | null;
  connected_at?: string | null;
  token_expires_at?: string | null;
  last_error?: string | null;
};

export type FinanceTikTokConnectionResponse = {
  connected: boolean;
  status: string;
  selected_advertiser_id?: string | null;
  selected_advertiser_name?: string | null;
  advertiser_ids?: string[] | null;
  connected_at?: string | null;
  token_expires_at?: string | null;
  refresh_token_expires_at?: string | null;
  last_error?: string | null;
};

export type FinanceGoogleAdsConnectionResponse = {
  connected: boolean;
  status: string;
  selected_customer_id?: string | null;
  selected_customer_name?: string | null;
  customer_ids?: string[] | null;
  manager_customer_id?: string | null;
  connected_at?: string | null;
  token_expires_at?: string | null;
  last_error?: string | null;
};

type FinanceMetaOAuthUrlApiResponse = {
  authorization_url?: string;
  authorizationUrl?: string;
};

export type FinanceMetaOAuthUrlResponse = {
  authorizationUrl: string;
};

type FinanceTikTokOAuthUrlApiResponse = FinanceMetaOAuthUrlApiResponse;

export type FinanceTikTokOAuthUrlResponse = FinanceMetaOAuthUrlResponse;

type FinanceGoogleAdsOAuthUrlApiResponse = FinanceMetaOAuthUrlApiResponse;

export type FinanceGoogleAdsOAuthUrlResponse = FinanceMetaOAuthUrlResponse;

type FinanceFixedCostsResponse = {
  currency?: string;
  sourceRowsCount?: number;
  groupedCostsCount?: number;
  costs: FixedCost[];
};

export type FinanceCompanyProfile = {
  company_name: string;
  logo_url?: string | null;
};

export type FinanceAdAccountState = "active" | "paused" | "archived";
export type FinanceAdAccountStateReason =
  | "user_paused"
  | "access_removed"
  | "business_changed"
  | "";

export type FinanceMetaAdAccount = {
  id: string;
  name?: string;
  account_id?: string | null;
  account_status?: number | string | null;
  currency?: string | null;
  timezone_name?: string | null;
  state?: FinanceAdAccountState;
  state_reason?: FinanceAdAccountStateReason;
  last_synced_at?: string | null;
};

export type FinanceTikTokAdvertiser = {
  advertiser_id: string;
  advertiser_name?: string;
  name?: string;
  currency?: string | null;
  timezone?: string | null;
  state?: FinanceAdAccountState;
  state_reason?: FinanceAdAccountStateReason;
  last_synced_at?: string | null;
};

export type FinanceGoogleAdsCustomer = {
  id: string;
  name?: string | null;
  currency?: string | null;
  timezone?: string | null;
  manager?: boolean | null;
  test?: boolean | null;
  state?: FinanceAdAccountState;
  state_reason?: FinanceAdAccountStateReason;
  last_synced_at?: string | null;
};

type FinanceMetaAdAccountsApiResponse = {
  ok?: boolean;
  adaccounts?: FinanceMetaAdAccount[];
  data?: FinanceMetaAdAccount[];
};

type FinanceTikTokAdAccountsApiResponse = {
  ok?: boolean;
  adaccounts?: FinanceTikTokAdvertiser[];
  data?: {
    list?: FinanceTikTokAdvertiser[];
    advertisers?: FinanceTikTokAdvertiser[];
  } | FinanceTikTokAdvertiser[];
};

type FinanceGoogleAdsCustomersApiResponse = {
  ok?: boolean;
  adaccounts?: FinanceGoogleAdsCustomer[];
  data?: FinanceGoogleAdsCustomer[];
};

export type FinanceMetaBackfillResponse = {
  ok: boolean;
  status: string;
  sync_run_id?: number | null;
  syncRunId?: number | null;
  message?: string | null;
  auto_match?: FinanceAutoMatchSummary | null;
  autoMatch?: FinanceAutoMatchSummary | null;
};

export type FinanceTikTokBackfillResponse = {
  ok: boolean;
  status: string;
  sync_run_id?: number | null;
  syncRunId?: number | null;
  message?: string | null;
  auto_match?: FinanceAutoMatchSummary | null;
  autoMatch?: FinanceAutoMatchSummary | null;
};

export type FinanceGoogleAdsBackfillResponse = FinanceTikTokBackfillResponse;

export type FinanceAutoMatchSummary = {
  funnels_detected?: number;
  funnelsDetected?: number;
  meta_campaigns_matched?: number;
  metaCampaignsMatched?: number;
  meta_funnels_matched?: number;
  metaFunnelsMatched?: number;
  meta_campaigns_ambiguous?: number;
  metaCampaignsAmbiguous?: number;
  tiktok_campaigns_matched?: number;
  tiktokCampaignsMatched?: number;
  tiktok_funnels_matched?: number;
  tiktokFunnelsMatched?: number;
  tiktok_campaigns_ambiguous?: number;
  tiktokCampaignsAmbiguous?: number;
  google_ads_campaigns_matched?: number;
  googleAdsCampaignsMatched?: number;
  google_ads_funnels_matched?: number;
  googleAdsFunnelsMatched?: number;
  google_ads_campaigns_ambiguous?: number;
  googleAdsCampaignsAmbiguous?: number;
  dropi_orders_matched?: number;
  dropiOrdersMatched?: number;
  dropi_orders_unmatched?: number;
  dropiOrdersUnmatched?: number;
  pending_funnels?: number;
  pendingFunnels?: number;
};

export type FinanceDropiBackfillResponse = {
  ok: boolean;
  status: string;
  sync_run_id?: number | null;
  syncRunId?: number | null;
  orders_count: number;
  ordersCount?: number;
  matched_orders_count?: number;
  matchedOrdersCount?: number;
  unmatched_orders_count?: number;
  unmatchedOrdersCount?: number;
  partial?: boolean;
  rate_limited_segments?: number;
  rateLimitedSegments?: number;
  failed_segments?: number;
  failedSegments?: number;
  message: string;
};

export type FinanceMetaCampaignPerformance = {
  id: string;
  name: string;
  provider?: FinanceMarketingProvider;
  source_campaign_id?: string;
  ad_account_id?: string | null;
  advertiser_id?: string | null;
  customer_id?: string | null;
  objective?: string | null;
  effective_status?: string | null;
  daily_budget?: number | null;
  lifetime_budget?: number | null;
  spend: number;
  impressions: number;
  reach?: number;
  frequency?: number;
  clicks: number;
  ctr?: number;
  cpc?: number;
  cpm?: number;
  results?: number;
  cost_per_result?: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  purchase_roas?: number;
  video_views?: number;
  video_thruplay?: number;
  finance_revenue?: number;
  finance_orders?: number;
  matched_funnel_ids: string[];
};

export type FinanceMetaAdPerformance = {
  id: string;
  name: string;
  provider?: FinanceMarketingProvider;
  source_ad_id?: string;
  ad_account_id?: string | null;
  advertiser_id?: string | null;
  customer_id?: string | null;
  campaign_id: string;
  source_campaign_id?: string;
  campaign_name?: string | null;
  spend: number;
  impressions: number;
  reach: number;
  frequency: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_result: number;
  results?: number;
  purchase_roas?: number;
  video_views?: number;
  video_thruplay?: number;
  last_activity?: string | null;
};

export type FinanceFunnelSourceSignal = {
  funnel_id: string;
  funnel_name?: string | null;
  public_url?: string | null;
  subdomain?: string | null;
  primary_product_id?: string | null;
  meta_pixel_id?: string | null;
  fluxi_pixel_id?: string | null;
  google_pixel_id?: string | null;
  tiktok_pixel_id?: string | null;
  dropi_connected?: boolean;
  dropi_automatic_order?: boolean;
  dropi_products_count?: number;
  mastershop_connected?: boolean;
  mastershop_automatic_order?: boolean;
  mastershop_products_count?: number;
  primary_public_url?: string | null;
  primary_host?: string | null;
  legacy_onlinesl_url?: string | null;
  legacy_onlinesl_host?: string | null;
  candidate_urls?: string[];
};

export type FinanceDropiProductMatch = {
  id: string;
  dropi_id: string;
};

export type FinanceMastershopProductMatch = {
  id: string;
  mastershop_id: string;
  mastershop_variant_id?: string | null;
  external_product_info?: FinanceMastershopExternalProductInfo | null;
};

export type FinanceMastershopExternalProductInfo = {
  provider_id?: string;
  external_id?: string;
  name?: string | null;
  image?: string | null;
  price?: number | string | null;
  currency?: string | null;
  variant?: {
    id?: string | null;
    name?: string | null;
    price?: number | string | null;
    stock?: number | null;
  } | null;
  variants_count?: number | null;
  [key: string]: unknown;
};

export type FinanceMastershopCatalogVariant = {
  id: string;
  name?: string | null;
  sku?: string | null;
  price?: number | string | null;
  suggested_price?: number | string | null;
  stock?: number | null;
};

export type FinanceDropiIntegrationResponse = {
  automatic_order_dropi?: boolean;
  products?: FinanceDropiProductMatch[];
  compatible_with_funnel?: boolean;
  compatibility_reason?: string | null;
};

export type FinanceMastershopIntegrationResponse = {
  automatic_order_mastershop?: boolean;
  products?: FinanceMastershopProductMatch[];
};

export type FinanceMastershopCatalogProduct = {
  id: string;
  name: string;
  description?: string | null;
  image?: string | null;
  price?: number | string | null;
  suggested_price?: number | string | null;
  currency?: string | null;
  stock?: number | null;
  category?: string | null;
  variants?: FinanceMastershopCatalogVariant[];
};

export type FinanceMastershopProductsResponse = {
  page: number;
  limit: number;
  total: number;
  products: FinanceMastershopCatalogProduct[];
};

export type FinanceDropiConnectionResponse = {
  connected: boolean;
  status: string;
  country_code?: string | null;
  is_active?: boolean;
};

export type FinanceFunnelWebsiteProduct = {
  id: string;
  name: string;
  type?: string | null;
  image?: string | null;
};

type FinanceWebsiteProductApiRow = {
  product_id?: string;
  productId?: string;
  type?: string | null;
};

type FinanceWebsiteProductInfoApiRow = {
  id?: string;
  name?: string;
  image?: string | null;
};

type FinanceWebsiteDetailApiResponse = {
  website?: {
    products?: FinanceWebsiteProductApiRow[];
  };
  products_info?: FinanceWebsiteProductInfoApiRow[];
  productsInfo?: FinanceWebsiteProductInfoApiRow[];
};

type FinanceCampaignRow = {
  id: string;
  name: string;
  status: "ACTIVE" | "PAUSED" | "OTHER";
  spend: number;
  revenue: number;
  purchases: number;
  roas: number;
  cxc: number;
  ctr: number;
  lastActivity: string;
};

type MetaAdRow = {
  ad_id?: string;
  ad_name?: string;
  campaign_name?: string;
  effective_status?: string;
  spend?: number;
  purchases?: number;
  roas?: number;
  cost_per_result?: number;
  ctr?: number;
  results?: number;
  updated_time?: string;
};

type FinanceFunnelApiRow = {
  id: string;
  name: string;
  product_name?: string;
  productName?: string;
  product_image_url?: string;
  productImageUrl?: string;
  is_active?: boolean;
  isActive?: boolean;
  is_archived?: boolean;
  isArchived?: boolean;
  id_sequence_owner_id?: number | null;
  idSequenceOwnerId?: number | null;
  meta_status?: Funnel["metaStatus"];
  metaStatus?: Funnel["metaStatus"];
  metrics?: {
    profit?: number | null;
    sales_count?: number | null;
    salesCount?: number | null;
    sales_value?: number | null;
    salesValue?: number | null;
    ad_spend?: number | null;
    adSpend?: number | null;
    cpa?: number | null;
  };
  matched_campaign_ids?: string[];
  matchedCampaignIds?: string[];
};

type FinanceCompanySummaryResponse = Partial<CompanyData>;
type FinanceFunnelDetailResponse = FunnelDetailData;

export type FinanceLogisticsSummary = {
  total_orders: number;
  dropi_orders: number;
  matched_orders: number;
  unmatched_orders: number;
  matched_value: number;
  unmatched_value: number;
  matched_product_cost: number;
  unmatched_product_cost: number;
  matched_shipping_cost: number;
  unmatched_shipping_cost: number;
  with_guide: number;
  in_transit: number;
  delivered: number;
  returned: number;
  incidents: number;
  delivery_rate: number;
  return_rate: number;
  value_at_risk: number;
  product_cost: number;
  shipping_cost: number;
  fulfillment_cost: number;
  platform_commission: number;
  return_cost: number;
  variable_cost: number;
  currency: string;
};

export type FinanceLogisticsStatusBucket = {
  id: string;
  label: string;
  count: number;
  value: number;
};

export type FinanceLogisticsMapOrder = {
  order_id: number;
  order_hash_id?: string | null;
  status: string;
  status_label: string;
  city?: string | null;
  department?: string | null;
  latitude: number;
  longitude: number;
  carrier?: string | null;
  guide_number?: string | null;
  value: number;
};

export type FinanceLogisticsMapBubble = {
  key: string;
  label: string;
  status: string;
  status_label: string;
  city?: string | null;
  department?: string | null;
  latitude: number;
  longitude: number;
  count: number;
  value: number;
};

export type FinanceLogisticsOrder = {
  order_id: number;
  order_hash_id?: string | null;
  funnel_id?: string | null;
  sales_channel?: string | null;
  status: string;
  status_label: string;
  order_state?: string | null;
  logistic_state?: string | null;
  dropi_state?: string | null;
  dropi_order_id?: number | null;
  guide_number?: string | null;
  carrier?: string | null;
  sticker_file?: string | null;
  customer_name?: string | null;
  city?: string | null;
  department?: string | null;
  product_names?: string | null;
  units: number;
  value: number;
  total_paid?: number;
  totalPaid?: number;
  product_cost?: number;
  shipping_cost?: number;
  fulfillment_cost?: number;
  platform_commission?: number;
  return_cost?: number;
  variable_cost?: number;
  currency: string;
  created_at: string;
  last_tracking_at?: string | null;
  last_error?: string | null;
};

export type FinanceLogisticsCarrierSource = {
  id: string;
  name: string;
  status: string;
  tracking_source: string;
  notes: string;
  docs_url?: string | null;
};

export type FinanceLogisticsOverview = {
  dropi_connected: boolean;
  dropi_status: string;
  period: Period;
  summary: FinanceLogisticsSummary;
  status_breakdown: FinanceLogisticsStatusBucket[];
  map_orders: FinanceLogisticsMapOrder[];
  map_bubbles?: FinanceLogisticsMapBubble[];
  orders: FinanceLogisticsOrder[];
  carrier_sources: FinanceLogisticsCarrierSource[];
};

export type FinanceReportChannel = "email" | "whatsapp";

export type FinanceReportRecipient = {
  label: string;
  email?: string;
  phone?: string;
};

export type FinanceReportRequest = {
  period: Period;
  frequency: string;
  send_time: string;
  channels: FinanceReportChannel[];
  sections: string[];
  recipients: FinanceReportRecipient[];
};

export type FinanceReportConfig = {
  configured: boolean;
  frequency: string;
  send_time: string;
  country_code: string;
  timezone?: string;
  weekly_day?: number;
  monthly_day?: number;
  channels: FinanceReportChannel[];
  sections: string[];
  recipients: FinanceReportRecipient[];
};

export type FinanceReportDelivery = {
  channel: FinanceReportChannel;
  status: "ready" | "sent" | "partial" | "pending_provider" | "missing_recipient" | "failed";
  message: string;
};

export type FinanceReportPreview = {
  ok: boolean;
  title: string;
  executive_summary: string;
  sections: Array<{
    id: string;
    title: string;
    body: string;
    metric?: string;
  }>;
  deliveries: FinanceReportDelivery[];
};

export type FinanceMarketingProvider = "meta" | "tiktok" | "google_ads";

const getFinanceRequestErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  const message = data?.message?.trim();
  return message || fallback;
};

export const getFinanceStatus = async (): Promise<FinanceStatusResponse> => {
  try {
    const response = await axiosInstanceWebsite.get<FinanceStatusResponse>("/finance/status");
    return {
      backend_available: true,
      finance_enabled: Boolean(response.data.finance_enabled),
      finance_activated_at: response.data.finance_activated_at ?? null,
      meta_connected: Boolean(response.data.meta_connected),
      tiktok_connected: Boolean(response.data.tiktok_connected),
      google_ads_connected: Boolean(response.data.google_ads_connected),
      dropi_connected: Boolean(response.data.dropi_connected),
      selected_google_ads_customer_id: response.data.selected_google_ads_customer_id ?? null,
    };
  } catch (error) {
    throw new Error(
      getFinanceRequestErrorMessage(
        error,
        "No pudimos consultar el estado de Finance. Intenta de nuevo."
      )
    );
  }
};

export const activateFinance = async (): Promise<FinanceStatusResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceStatusResponse>("/finance/activate");

  return {
    backend_available: true,
    finance_enabled: Boolean(response.data.finance_enabled),
    finance_activated_at: response.data.finance_activated_at ?? null,
    meta_connected: Boolean(response.data.meta_connected),
    tiktok_connected: Boolean(response.data.tiktok_connected),
    google_ads_connected: Boolean(response.data.google_ads_connected),
    dropi_connected: Boolean(response.data.dropi_connected),
    selected_google_ads_customer_id: response.data.selected_google_ads_customer_id ?? null,
  };
};

export const getFinanceMetaConnection = async (): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaConnectionResponse>(
    "/finance/meta/connection"
  );
  return response.data;
};

export const getFinanceTikTokConnection = async (): Promise<FinanceTikTokConnectionResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceTikTokConnectionResponse>(
    "/finance/tiktok/connection"
  );
  return response.data;
};

export const getFinanceGoogleAdsConnection = async (): Promise<FinanceGoogleAdsConnectionResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceGoogleAdsConnectionResponse>(
    "/finance/google-ads/connection"
  );
  return response.data;
};

export const getFinanceDropiConnection = async (): Promise<FinanceDropiConnectionResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceDropiConnectionResponse>(
    "/finance/dropi/connection"
  );
  return {
    connected: Boolean(response.data.connected),
    status: response.data.status ?? "unknown",
    country_code: response.data.country_code ?? null,
    is_active: Boolean(response.data.is_active),
  };
};

export const getFinanceMetaOAuthUrl = async (): Promise<FinanceMetaOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaOAuthUrlApiResponse>(
    "/finance/meta/oauth/url"
  );
  return {
    authorizationUrl:
      response.data.authorization_url ??
      response.data.authorizationUrl ??
      "",
  };
};

export const getFinanceTikTokOAuthUrl = async (): Promise<FinanceTikTokOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceTikTokOAuthUrlApiResponse>(
    "/finance/tiktok/oauth/url"
  );
  return {
    authorizationUrl:
      response.data.authorization_url ??
      response.data.authorizationUrl ??
      "",
  };
};

export const getFinanceGoogleAdsOAuthUrl = async (): Promise<FinanceGoogleAdsOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceGoogleAdsOAuthUrlApiResponse>(
    "/finance/google-ads/oauth/url"
  );
  return {
    authorizationUrl:
      response.data.authorization_url ??
      response.data.authorizationUrl ??
      "",
  };
};

export const getFinanceMetaAdAccounts = async (businessId?: string): Promise<FinanceMetaAdAccount[]> => {
  const response = await axiosInstanceWebsite.get<FinanceMetaAdAccountsApiResponse>(
    "/finance/meta/ad-accounts",
    { params: businessId ? { business_id: businessId } : undefined }
  );
  return response.data.adaccounts ?? response.data.data ?? [];
};

export const getFinanceTikTokAdAccounts = async (): Promise<FinanceTikTokAdvertiser[]> => {
  const response = await axiosInstanceWebsite.get<FinanceTikTokAdAccountsApiResponse>(
    "/finance/tiktok/ad-accounts"
  );
  const data = response.data.data;
  if (Array.isArray(data)) return data;
  return response.data.adaccounts ?? data?.list ?? data?.advertisers ?? [];
};

export const getFinanceGoogleAdsAdAccounts = async (): Promise<FinanceGoogleAdsCustomer[]> => {
  const response = await axiosInstanceWebsite.get<FinanceGoogleAdsCustomersApiResponse>(
    "/finance/google-ads/ad-accounts"
  );
  return response.data.adaccounts ?? response.data.data ?? [];
};

export const updateFinanceMetaAdAccount = async (
  adAccountIds: string | string[],
  adAccountName?: string
): Promise<FinanceMetaConnectionResponse> => {
  const selectedAccountIds = Array.isArray(adAccountIds)
    ? adAccountIds
    : [adAccountIds];
  const cleanAccountIds = selectedAccountIds
    .map((accountId) => accountId.trim())
    .filter((accountId) => accountId.length > 0);

  const response = await axiosInstanceWebsite.put<FinanceMetaConnectionResponse>(
    "/finance/meta/ad-account",
    {
      ad_account_id: cleanAccountIds[0] ?? "",
      ad_account_ids: cleanAccountIds,
      ad_account_name: cleanAccountIds.length === 1 ? adAccountName : undefined,
    }
  );
  return response.data;
};

export const updateFinanceMetaAdAccountState = async (
  adAccountId: string,
  paused: boolean
): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceMetaConnectionResponse>(
    "/finance/meta/ad-account/state",
    {
      ad_account_id: adAccountId.trim(),
      paused,
    }
  );
  return response.data;
};

export const deleteFinanceMetaAdAccountData = async (
  adAccountId: string
): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceMetaConnectionResponse>(
    "/finance/meta/ad-account/data",
    { params: { ad_account_id: adAccountId.trim() } }
  );
  return response.data;
};

export const updateFinanceTikTokAdAccount = async (
  advertiserId: string,
  advertiserName?: string
): Promise<FinanceTikTokConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceTikTokConnectionResponse>(
    "/finance/tiktok/ad-account",
    {
      advertiser_id: advertiserId.trim(),
      advertiser_name: advertiserName,
    }
  );
  return response.data;
};

export const updateFinanceGoogleAdsAdAccount = async (
  customerId: string,
  customerName?: string
): Promise<FinanceGoogleAdsConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceGoogleAdsConnectionResponse>(
    "/finance/google-ads/ad-account",
    {
      customer_id: customerId.trim(),
      customer_name: customerName,
    }
  );
  return response.data;
};

export const updateFinanceTikTokAdvertiserState = async (
  advertiserId: string,
  paused: boolean
): Promise<FinanceTikTokConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceTikTokConnectionResponse>(
    "/finance/tiktok/ad-account/state",
    { advertiser_id: advertiserId.trim(), paused }
  );
  return response.data;
};

export const deleteFinanceTikTokAdvertiserData = async (
  advertiserId: string
): Promise<FinanceTikTokConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceTikTokConnectionResponse>(
    "/finance/tiktok/ad-account/data",
    { params: { advertiser_id: advertiserId.trim() } }
  );
  return response.data;
};

export const updateFinanceGoogleAdsCustomerState = async (
  customerId: string,
  paused: boolean
): Promise<FinanceGoogleAdsConnectionResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceGoogleAdsConnectionResponse>(
    "/finance/google-ads/ad-account/state",
    { customer_id: customerId.trim(), paused }
  );
  return response.data;
};

export const deleteFinanceGoogleAdsCustomerData = async (
  customerId: string
): Promise<FinanceGoogleAdsConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceGoogleAdsConnectionResponse>(
    "/finance/google-ads/ad-account/data",
    { params: { customer_id: customerId.trim() } }
  );
  return response.data;
};

export const disconnectFinanceMetaConnection = async (): Promise<FinanceMetaConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceMetaConnectionResponse>("/finance/meta/connection");
  return response.data;
};

export const disconnectFinanceTikTokConnection = async (): Promise<FinanceTikTokConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceTikTokConnectionResponse>("/finance/tiktok/connection");
  return response.data;
};

export const disconnectFinanceGoogleAdsConnection = async (): Promise<FinanceGoogleAdsConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceGoogleAdsConnectionResponse>("/finance/google-ads/connection");
  return response.data;
};

export const clearFinanceDropiData = async (): Promise<FinanceDropiConnectionResponse> => {
  const response = await axiosInstanceWebsite.delete<FinanceDropiConnectionResponse>("/finance/dropi/data");
  return response.data;
};

export const startTikTokBackfill = async (
  options?: { resetManualMatches?: boolean }
): Promise<FinanceTikTokBackfillResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceTikTokBackfillResponse>(
    "/finance/tiktok/backfills",
    options?.resetManualMatches ? { reset_manual_matches: true } : {}
  );
  return response.data;
};

export const startGoogleAdsBackfill = async (
  options?: { resetManualMatches?: boolean }
): Promise<FinanceGoogleAdsBackfillResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceGoogleAdsBackfillResponse>(
    "/finance/google-ads/backfills",
    options?.resetManualMatches ? { reset_manual_matches: true } : {}
  );
  return response.data;
};

export const getFunnels = async (dateRange?: DateRange): Promise<Funnel[]> => {
  const params = dateRange
    ? {
        period: "custom",
        from: formatDateForApi(dateRange.initialDate),
        to: formatDateForApi(dateRange.finalDate),
      }
    : undefined;
  const response = await axiosInstanceWebsite.get<FinanceFunnelApiRow[]>("/finance/funnels", {
    params,
  });
  return response.data.map((item) => ({
    id: item.id,
    name: item.name,
    productName: item.product_name ?? item.productName ?? "Producto sin nombre",
    productImageUrl: item.product_image_url ?? item.productImageUrl ?? "",
    isActive: item.is_active ?? item.isActive ?? true,
    isArchived: item.is_archived ?? item.isArchived ?? false,
    idSequenceOwnerId: item.id_sequence_owner_id ?? item.idSequenceOwnerId ?? null,
    metaStatus: item.meta_status ?? item.metaStatus ?? "unconfigured",
    metrics: {
      profit: item.metrics?.profit ?? null,
      salesCount: item.metrics?.sales_count ?? item.metrics?.salesCount ?? null,
      salesValue: item.metrics?.sales_value ?? item.metrics?.salesValue ?? null,
      adSpend: item.metrics?.ad_spend ?? item.metrics?.adSpend ?? null,
      cpa: item.metrics?.cpa ?? null,
    },
    matchedCampaignIds: item.matched_campaign_ids ?? item.matchedCampaignIds ?? [],
  }));
};

export const listFinanceFunnels = getFunnels;

export const getMetaCampaigns = async (): Promise<MetaCampaign[]> => {
  return getMarketingCampaigns();
};

export const getMarketingCampaigns = async (): Promise<MetaCampaign[]> => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const params = { from: formatDate(from), to: formatDate(to) };
  const [metaItems, tiktokItems, googleAdsItems] = await Promise.all([
    fetchCampaignPerformanceSource("/finance/meta/campaigns", params, "meta"),
    fetchCampaignPerformanceSource("/finance/tiktok/campaigns", params, "tiktok"),
    fetchCampaignPerformanceSource("/finance/google-ads/campaigns", params, "google_ads"),
  ]);

  return [...metaItems, ...tiktokItems, ...googleAdsItems].map((item) => ({
    id: item.id,
    provider: item.provider,
    name: item.name,
    status: item.effective_status === "ACTIVE" ? "ACTIVE" : "PAUSED",
    spend: Number(item.spend ?? 0),
    roas: item.roas === 0 && item.spend === 0 ? null : Number(item.roas ?? 0),
    matchedFunnelIds: item.matched_funnel_ids ?? [],
  }));
};

export const matchCampaignsToFunnel = async (
  funnelId: string,
  campaignIds: string[]
): Promise<void> => {
  await axiosInstanceWebsite.post(
    `/finance/funnels/${encodeURIComponent(funnelId)}/match-campaigns`,
    { campaign_ids: campaignIds }
  );
};

export const getFinanceFunnelSources = async (): Promise<FinanceFunnelSourceSignal[]> => {
  const response = await axiosInstanceWebsite.get<FinanceFunnelSourceSignal[]>(
    "/finance/funnels/sources"
  );
  return response.data.map((source) => ({
    ...source,
    candidate_urls: source.candidate_urls ?? [],
  }));
};

const productTypeLabel = (type?: string | null) => {
  if (type === "PRINCIPAL") return "Producto principal";
  if (type === "UP_SELL") return "Upsell";
  if (type === "DOWN_SELL") return "Downsell";
  if (type === "BUMP_OFFER") return "Order bump";
  return "Producto";
};

export const getFunnelWebsiteProducts = async (
  funnelId: string
): Promise<FinanceFunnelWebsiteProduct[]> => {
  const response = await axiosInstanceWebsite.get<FinanceWebsiteDetailApiResponse>(
    `/websites/${encodeURIComponent(funnelId)}`
  );
  const websiteProducts = response.data.website?.products ?? [];
  const productInfo = response.data.products_info ?? response.data.productsInfo ?? [];
  const productInfoById = new Map<string, FinanceWebsiteProductInfoApiRow>();
  productInfo.forEach((product) => {
    if (product.id) productInfoById.set(product.id, product);
  });

  return websiteProducts
    .map((product) => {
      const id = product.product_id ?? product.productId ?? "";
      const info = productInfoById.get(id);
      return {
        id,
        name: info?.name ?? productTypeLabel(product.type),
        type: product.type ?? null,
        image: info?.image ?? null,
      };
    })
    .filter((product) => product.id.trim().length > 0);
};

export const getFunnelDropiIntegration = async (
  funnelId: string
): Promise<FinanceDropiIntegrationResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceDropiIntegrationResponse>(
    `/websites/${encodeURIComponent(funnelId)}/dropi-integration`
  );
  return {
    ...response.data,
    products: response.data.products ?? [],
  };
};

export const updateFunnelDropiIntegration = async (
  funnelId: string,
  payload: {
    automatic_order_dropi: boolean;
    products: FinanceDropiProductMatch[];
  }
): Promise<FinanceDropiIntegrationResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceDropiIntegrationResponse>(
    `/websites/${encodeURIComponent(funnelId)}/dropi-integration`,
    payload
  );
  return {
    ...response.data,
    products: response.data.products ?? [],
  };
};

export const getMastershopProducts = async (
  search = ""
): Promise<FinanceMastershopProductsResponse> => {
  const params = new URLSearchParams();
  params.set("page", "1");
  params.set("limit", "50");
  if (search.trim()) params.set("search", search.trim());

  const response = await axiosInstanceWebsite.get<FinanceMastershopProductsResponse>(
    `/order/mastershop/products?${params.toString()}`
  );
  return {
    ...response.data,
    products: response.data.products ?? [],
  };
};

export const getFunnelMastershopIntegration = async (
  funnelId: string
): Promise<FinanceMastershopIntegrationResponse> => {
  const response = await axiosInstanceWebsite.get<FinanceMastershopIntegrationResponse>(
    `/websites/${encodeURIComponent(funnelId)}/mastershop-integration`
  );
  return {
    ...response.data,
    products: response.data.products ?? [],
  };
};

export const updateFunnelMastershopIntegration = async (
  funnelId: string,
  payload: {
    automatic_order_mastershop: boolean;
    products: FinanceMastershopProductMatch[];
  }
): Promise<FinanceMastershopIntegrationResponse> => {
  const response = await axiosInstanceWebsite.put<FinanceMastershopIntegrationResponse>(
    `/websites/${encodeURIComponent(funnelId)}/mastershop-integration`,
    payload
  );
  return {
    ...response.data,
    products: response.data.products ?? [],
  };
};

const buildPeriodParams = (period: Period) => {
  if (period.id === "all-time") {
    return { period: period.id };
  }

  return {
    period: period.id,
    from: formatDateForApi(period.from),
    to: formatDateForApi(period.to),
  };
};

const buildDateRangeParams = (period: Period) => ({
  from: formatDateForApi(period.from),
  to: formatDateForApi(period.to),
});

const withProviderCampaignId = (id: string, provider: FinanceMarketingProvider) => {
  if (provider === "tiktok") return `tiktok:${id}`;
  if (provider === "google_ads") return `google_ads:${id}`;
  return id;
};

const withoutProviderPrefix = (id: string) =>
  id.startsWith("google_ads:")
    ? id.slice("google_ads:".length)
    : id.startsWith("tiktok:")
    ? id.slice("tiktok:".length)
    : id.startsWith("meta:")
    ? id.slice("meta:".length)
    : id;

const fetchCampaignPerformanceSource = async (
  endpoint: string,
  params: Record<string, string | undefined>,
  provider: FinanceMarketingProvider
): Promise<FinanceMetaCampaignPerformance[]> => {
  try {
    const response = await axiosInstanceWebsite.get<{
      items?: FinanceMetaCampaignPerformance[];
    }>(endpoint, { params });

    return (response.data.items ?? []).map((item) => {
      const sourceCampaignId = withoutProviderPrefix(item.source_campaign_id ?? item.id);
      return {
        ...item,
        id: withProviderCampaignId(sourceCampaignId, provider),
        source_campaign_id: sourceCampaignId,
        provider,
        spend: Number(item.spend ?? 0),
        impressions: Number(item.impressions ?? 0),
        reach: Number(item.reach ?? 0),
        frequency: Number(item.frequency ?? 0),
        clicks: Number(item.clicks ?? 0),
        ctr: Number(item.ctr ?? 0),
        cpc: Number(item.cpc ?? 0),
        cpm: Number(item.cpm ?? 0),
        results: Number(item.results ?? 0),
        cost_per_result: Number(item.cost_per_result ?? 0),
        purchases: Number(item.purchases ?? 0),
        purchase_value: Number(item.purchase_value ?? 0),
        roas: Number(item.roas ?? 0),
        purchase_roas: Number(item.purchase_roas ?? 0),
        video_views: Number(item.video_views ?? 0),
        video_thruplay: Number(item.video_thruplay ?? 0),
        finance_revenue: Number(item.finance_revenue ?? 0),
        finance_orders: Number(item.finance_orders ?? 0),
        matched_funnel_ids: item.matched_funnel_ids ?? [],
      };
    });
  } catch {
    return [];
  }
};

const fetchAdPerformanceSource = async (
  endpoint: string,
  params: Record<string, string | undefined>,
  provider: FinanceMarketingProvider
): Promise<FinanceMetaAdPerformance[]> => {
  try {
    const response = await axiosInstanceWebsite.get<{
      items?: FinanceMetaAdPerformance[];
    }>(endpoint, { params });

    return (response.data.items ?? []).map((item) => {
      const sourceCampaignId = withoutProviderPrefix(item.source_campaign_id ?? item.campaign_id);
      const sourceAdId = withoutProviderPrefix(item.source_ad_id ?? item.id);
      return {
        ...item,
        id: provider === "tiktok" ? `tiktok:${sourceAdId}` : provider === "google_ads" ? `google_ads:${sourceAdId}` : sourceAdId,
        source_ad_id: sourceAdId,
        provider,
        campaign_id: withProviderCampaignId(sourceCampaignId, provider),
        source_campaign_id: sourceCampaignId,
        spend: Number(item.spend ?? 0),
        impressions: Number(item.impressions ?? 0),
        reach: Number(item.reach ?? 0),
        frequency: Number(item.frequency ?? 0),
        clicks: Number(item.clicks ?? 0),
        purchases: Number(item.purchases ?? 0),
        purchase_value: Number(item.purchase_value ?? 0),
        roas: Number(item.roas ?? 0),
        ctr: Number(item.ctr ?? 0),
        cpc: Number(item.cpc ?? 0),
        cpm: Number(item.cpm ?? 0),
        cost_per_result: Number(item.cost_per_result ?? 0),
        results: Number(item.results ?? 0),
        purchase_roas: Number(item.purchase_roas ?? 0),
        video_views: Number(item.video_views ?? 0),
        video_thruplay: Number(item.video_thruplay ?? 0),
      };
    });
  } catch {
    return [];
  }
};

export const getFinanceCampaignPerformance = async (
  period: Period
): Promise<FinanceMetaCampaignPerformance[]> => {
  const [metaItems, tiktokItems, googleAdsItems] = await Promise.all([
    fetchCampaignPerformanceSource("/finance/meta/campaigns", buildPeriodParams(period), "meta"),
    fetchCampaignPerformanceSource("/finance/tiktok/campaigns", buildDateRangeParams(period), "tiktok"),
    fetchCampaignPerformanceSource("/finance/google-ads/campaigns", buildDateRangeParams(period), "google_ads"),
  ]);
  return [...metaItems, ...tiktokItems, ...googleAdsItems];
};

export const getFinanceAdsPerformance = async (
  period: Period
): Promise<FinanceMetaAdPerformance[]> => {
  const [metaItems, tiktokItems, googleAdsItems] = await Promise.all([
    fetchAdPerformanceSource("/finance/meta/ads", buildPeriodParams(period), "meta"),
    fetchAdPerformanceSource("/finance/tiktok/ads", buildDateRangeParams(period), "tiktok"),
    fetchAdPerformanceSource("/finance/google-ads/ads", buildDateRangeParams(period), "google_ads"),
  ]);
  return [...metaItems, ...tiktokItems, ...googleAdsItems];
};

const emptyCompanyStages = (): CompanyData["stages"] => ({
  checkoutViews: 0,
  ordersCreated: 0,
  dispatched: 0,
  delivered: 0,
  returned: 0,
  ordersValue: 0,
  dispatchedValue: 0,
  deliveredValue: 0,
  returnedValue: 0,
});

const normalizeCompanySummary = (summary: FinanceCompanySummaryResponse): CompanyData => {
  const fixedCosts = summary.fixedCosts ?? [];
  const fixedCostsCurrency = summary.fixedCostsCurrency ?? fixedCosts[0]?.currency ?? "COP";
  const totalFixedCosts =
    summary.totalFixedCosts ?? fixedCosts.reduce((acc, cost) => acc + cost.amount, 0);
  const operatingProfit = summary.operatingProfit ?? 0;
  const paymentSummary = summary.revenueByPaymentMethod ?? {
    total: summary.totalRevenue ?? 0,
    cashOnDelivery: 0,
    prepaid: 0,
    other: 0,
    collected: 0,
  };
  const cpaTargets = summary.cpaTargets ?? {
    realCpa: summary.avgCpa ?? 0,
    targetCpa20: 0,
    breakEvenCpa: summary.avgMaxProfitableCpa ?? 0,
  };
  const totalProductCosts = Number(summary.totalProductCosts ?? 0);
  const totalVariableCosts = Number(summary.totalVariableCosts ?? 0);
  const rawTotalLogisticsCosts = Number(summary.totalLogisticsCosts ?? 0);
  const totalLogisticsCosts = rawTotalLogisticsCosts > 0 &&
    totalVariableCosts > 0 &&
    totalProductCosts > 0 &&
    Math.abs(rawTotalLogisticsCosts - totalVariableCosts) < 1
      ? Math.max(totalVariableCosts - totalProductCosts, 0)
      : rawTotalLogisticsCosts || (totalVariableCosts > 0 ? Math.max(totalVariableCosts - totalProductCosts, 0) : 0);
  const totalShippingCosts = Number(
    summary.totalShippingCosts ??
      Math.max(totalLogisticsCosts - Number(summary.totalFulfillmentCosts ?? 0) - Number(summary.totalPlatformCommission ?? 0) - Number(summary.totalReturnCosts ?? 0), 0)
  );
  const totalReturnShippingCosts = Number(summary.totalReturnShippingCosts ?? 0);
  const totalOutboundShippingCosts = Number(
    summary.totalOutboundShippingCosts ?? Math.max(totalShippingCosts - totalReturnShippingCosts, 0)
  );
  const estimatedPendingProfit = Number(summary.estimatedPendingProfit ?? 0);
  const grossAdInvestment =
    summary.grossAdInvestment ?? summary.grossMetaAdInvestment ?? summary.totalAdInvestment ?? 0;

  return {
    period: summary.period,
    netProfit: summary.netProfit ?? operatingProfit - totalFixedCosts,
    operatingProfit,
    avgRoas: summary.avgRoas ?? 0,
    avgCpa: summary.avgCpa ?? 0,
    totalRevenue: summary.totalRevenue ?? 0,
    grossRevenue: summary.grossRevenue ?? summary.revenueByPaymentMethod?.total ?? 0,
    collectedRevenue: summary.collectedRevenue ?? summary.totalRevenue ?? 0,
    totalVariableCosts: totalVariableCosts || totalProductCosts + totalLogisticsCosts,
    totalLogisticsCosts,
    totalProductCosts,
    totalShippingCosts,
    totalOutboundShippingCosts,
    totalReturnShippingCosts,
    totalFulfillmentCosts: Number(summary.totalFulfillmentCosts ?? 0),
    totalPlatformCommission: Number(summary.totalPlatformCommission ?? 0),
    totalReturnCosts: Number(summary.totalReturnCosts ?? 0),
    pendingRevenue: Number(summary.pendingRevenue ?? 0),
    pendingProductCosts: Number(summary.pendingProductCosts ?? 0),
    pendingLogisticsCosts: Number(summary.pendingLogisticsCosts ?? 0),
    estimatedPendingProfit,
    estimatedTotalProfit: Number(summary.estimatedTotalProfit ?? (summary.netProfit ?? operatingProfit - totalFixedCosts) + estimatedPendingProfit),
    totalAdInvestment: summary.totalAdInvestment ?? 0,
    grossAdInvestment,
    grossMetaAdInvestment: grossAdInvestment,
    matchedAdInvestment: summary.matchedAdInvestment ?? summary.totalAdInvestment ?? 0,
    unassignedAdInvestment: summary.unassignedAdInvestment ?? 0,
    fixedCosts: fixedCosts.map((cost) => ({ ...cost, currency: cost.currency ?? fixedCostsCurrency })),
    fixedCostsCurrency,
    totalFixedCosts,
    revenueByPaymentMethod: {
      total: Number(paymentSummary.total ?? 0),
      cashOnDelivery: Number(paymentSummary.cashOnDelivery ?? 0),
      prepaid: Number(paymentSummary.prepaid ?? 0),
      other: Number(paymentSummary.other ?? 0),
      collected: Number(paymentSummary.collected ?? 0),
    },
    funnels: (summary.funnels ?? []).map((funnel) => ({
      ...funnel,
      orders: Number(funnel.orders ?? 0),
      checkoutViews: Number(funnel.checkoutViews ?? 0),
    })),
    stages: summary.stages ?? emptyCompanyStages(),
    dailyData: summary.dailyData ?? [],
    cpaTargets: {
      realCpa: Number(cpaTargets.realCpa ?? 0),
      targetCpa20: Number(cpaTargets.targetCpa20 ?? 0),
      breakEvenCpa: Number(cpaTargets.breakEvenCpa ?? 0),
    },
    avgMaxProfitableCpa: summary.avgMaxProfitableCpa ?? 0,
  };
};

export const getCompanyFinanceSummary = async (period: Period): Promise<CompanyData> => {
  const response = await axiosInstanceWebsite.get<FinanceCompanySummaryResponse>(
    "/finance/company/summary",
    { params: buildPeriodParams(period) }
  );
  return normalizeCompanySummary(response.data);
};

export const getFinanceCompanyProfile = async (): Promise<FinanceCompanyProfile> => {
  const response = await axiosInstanceWebsite.get<FinanceCompanyProfile>("/finance/company/profile");
  return {
    company_name: response.data.company_name ?? "",
    logo_url: response.data.logo_url ?? null,
  };
};

export const saveFinanceCompanyProfile = async (
  profile: FinanceCompanyProfile
): Promise<FinanceCompanyProfile> => {
  const response = await axiosInstanceWebsite.put<FinanceCompanyProfile>(
    "/finance/company/profile",
    profile
  );
  return {
    company_name: response.data.company_name ?? "",
    logo_url: response.data.logo_url ?? null,
  };
};

const buildFixedCostsPayload = (costs: FixedCost[], currency = "COP") => ({
  currency,
  costs: costs.map((cost) => ({
    label: cost.label,
    amount: Number(cost.amount ?? 0),
  })),
});

export const saveFinanceFixedCosts = async (costs: FixedCost[], currency = "COP"): Promise<FixedCost[]> => {
  const response = await axiosInstanceWebsite.put<FinanceFixedCostsResponse>(
    "/finance/company/fixed-costs",
    buildFixedCostsPayload(costs, currency)
  );
  const responseCurrency = response.data.currency ?? currency;
  return (response.data.costs ?? []).map((cost) => ({ ...cost, currency: cost.currency ?? responseCurrency }));
};

export const previewFinanceFixedCostsImport = async (
  fileName: string,
  tableText: string,
  currency = "COP",
  sourceRowsCount = 0
): Promise<FinanceFixedCostsResponse> => {
  const response = await axiosInstanceWebsite.post<FinanceFixedCostsResponse>(
    "/finance/company/fixed-costs/imports/preview",
    {
      file_name: fileName,
      table_text: tableText,
      currency,
      source_rows_count: sourceRowsCount,
    }
  );
  return {
    ...response.data,
    currency: response.data.currency ?? currency,
    costs: (response.data.costs ?? []).map((cost) => ({
      ...cost,
      currency: cost.currency ?? response.data.currency ?? currency,
    })),
  };
};

export const getFunnelFinanceDetail = async (
  funnelId: string,
  period: Period
): Promise<FunnelDetailData> => {
  const response = await axiosInstanceWebsite.get<FinanceFunnelDetailResponse>(
    `/finance/funnels/${encodeURIComponent(funnelId)}/detail`,
    { params: buildPeriodParams(period) }
  );
  return {
    ...response.data,
    dailyData: response.data.dailyData ?? [],
  };
};

export const sendFinanceAssistantMessage = async (
  request: ChatRequest
): Promise<ChatResponse> => {
  const response = await axiosInstanceWebsite.post<ChatResponse>(
    "/finance/assistant/messages",
    request
  );
  return response.data;
};

export const getFinanceAssistantConversations = async (): Promise<ChatConversation[]> => {
  const response = await axiosInstanceWebsite.get<{ conversations: ChatConversation[] }>(
    "/finance/assistant/conversations"
  );
  return response.data.conversations ?? [];
};

export const getFinanceAssistantConversation = async (
  conversationId: string
): Promise<ChatConversationDetail> => {
  const response = await axiosInstanceWebsite.get<ChatConversationDetail>(
    `/finance/assistant/conversations/${encodeURIComponent(conversationId)}`
  );
  return response.data;
};

const buildReportPayload = (request: FinanceReportRequest) => ({
  period: request.period.id,
  from: formatDateForApi(request.period.from),
  to: formatDateForApi(request.period.to),
  frequency: request.frequency,
  send_time: request.send_time,
  channels: request.channels,
  sections: request.sections,
  recipients: request.recipients,
});

const normalizeReportConfig = (config: FinanceReportConfig): FinanceReportConfig => ({
  configured: Boolean(config.configured),
  frequency: config.frequency ?? "",
  send_time: config.send_time ?? "",
  country_code: config.country_code ?? "CO",
  timezone: config.timezone ?? "America/Bogota",
  weekly_day: Number(config.weekly_day ?? 1),
  monthly_day: Number(config.monthly_day ?? 1),
  channels: config.channels?.length ? config.channels : ["email"],
  sections: config.sections ?? [],
  recipients: config.recipients?.length ? config.recipients : [{ label: "", email: "", phone: "" }],
});

export const getFinanceReportConfig = async (): Promise<FinanceReportConfig> => {
  const response = await axiosInstanceWebsite.get<FinanceReportConfig>("/finance/reports/config");
  return normalizeReportConfig(response.data);
};

export const saveFinanceReportConfig = async (
  config: Omit<FinanceReportConfig, "configured">
): Promise<FinanceReportConfig> => {
  const response = await axiosInstanceWebsite.put<FinanceReportConfig>("/finance/reports/config", config);
  return normalizeReportConfig(response.data);
};

export const previewFinanceReport = async (
  request: FinanceReportRequest
): Promise<FinanceReportPreview> => {
  const response = await axiosInstanceWebsite.post<FinanceReportPreview>(
    "/finance/reports/preview",
    buildReportPayload(request)
  );
  return response.data;
};

export const sendFinanceReportTest = async (
  request: FinanceReportRequest
): Promise<FinanceReportPreview> => {
  const response = await axiosInstanceWebsite.post<FinanceReportPreview>(
    "/finance/reports/send-test",
    buildReportPayload(request)
  );
  return response.data;
};

const emptyLogisticsOverview = (period: Period): FinanceLogisticsOverview => ({
  dropi_connected: false,
  dropi_status: "unavailable",
  period,
  summary: {
    total_orders: 0,
    dropi_orders: 0,
    matched_orders: 0,
    unmatched_orders: 0,
    matched_value: 0,
    unmatched_value: 0,
    matched_product_cost: 0,
    unmatched_product_cost: 0,
    matched_shipping_cost: 0,
    unmatched_shipping_cost: 0,
    with_guide: 0,
    in_transit: 0,
    delivered: 0,
    returned: 0,
    incidents: 0,
    delivery_rate: 0,
    return_rate: 0,
    value_at_risk: 0,
    product_cost: 0,
    shipping_cost: 0,
    fulfillment_cost: 0,
    platform_commission: 0,
    return_cost: 0,
    variable_cost: 0,
    currency: "COP",
  },
  status_breakdown: [
    { id: "pending", label: "Pendiente", count: 0, value: 0 },
    { id: "guide_generated", label: "Guía generada", count: 0, value: 0 },
    { id: "in_transit", label: "En tránsito", count: 0, value: 0 },
    { id: "delivered", label: "Entregado", count: 0, value: 0 },
    { id: "returned", label: "Devuelto", count: 0, value: 0 },
    { id: "incident", label: "Novedad", count: 0, value: 0 },
    { id: "error", label: "Error", count: 0, value: 0 },
  ],
  map_orders: [],
  map_bubbles: [],
  orders: [],
  carrier_sources: [],
});

export const getFinanceLogisticsOverview = async (
  period: Period
): Promise<FinanceLogisticsOverview> => {
  try {
    const response = await axiosInstanceWebsite.get<FinanceLogisticsOverview>(
      "/finance/logistics/overview",
      { params: buildPeriodParams(period) }
    );
    const fallback = emptyLogisticsOverview(period);
    return {
      ...fallback,
      ...response.data,
      summary: {
        ...fallback.summary,
        ...(response.data.summary ?? {}),
      },
      status_breakdown: response.data.status_breakdown ?? fallback.status_breakdown,
      map_orders: response.data.map_orders ?? [],
      orders: response.data.orders ?? [],
      carrier_sources: response.data.carrier_sources ?? [],
    };
  } catch {
    return emptyLogisticsOverview(period);
  }
};

export const financeService = {
  getFunnels,
  getMetaCampaigns,
  getMarketingCampaigns,
  getFinanceMetaAdAccounts,
  getFinanceTikTokAdAccounts,
  getFinanceGoogleAdsAdAccounts,
  getFinanceTikTokConnection,
  getFinanceGoogleAdsConnection,
  getFinanceDropiConnection,
  disconnectFinanceMetaConnection,
  disconnectFinanceTikTokConnection,
  disconnectFinanceGoogleAdsConnection,
  clearFinanceDropiData,
  getFinanceCampaignPerformance,
  getFinanceAdsPerformance,
  getFinanceFunnelSources,
  getFinanceCompanyProfile,
  getFunnelWebsiteProducts,
  getFunnelDropiIntegration,
  getFunnelMastershopIntegration,
  getMastershopProducts,
  updateFunnelDropiIntegration,
  updateFunnelMastershopIntegration,
  updateFinanceMetaAdAccount,
  updateFinanceTikTokAdAccount,
  updateFinanceGoogleAdsAdAccount,
  startTikTokBackfill,
  startGoogleAdsBackfill,
  matchCampaignsToFunnel,
  saveFinanceFixedCosts,
  saveFinanceCompanyProfile,
  previewFinanceFixedCostsImport,
  sendFinanceAssistantMessage,
  getFinanceAssistantConversations,
  getFinanceAssistantConversation,
  previewFinanceReport,
  sendFinanceReportTest,
  getFinanceReportConfig,
  saveFinanceReportConfig,
  async startMetaBackfill(options?: { resetManualMatches?: boolean }): Promise<FinanceMetaBackfillResponse> {
    const response = await axiosInstanceWebsite.post<FinanceMetaBackfillResponse>(
      "/finance/meta/backfills",
      options?.resetManualMatches ? { reset_manual_matches: true } : {}
    );
    return response.data;
  },
  async startDropiBackfill(
    period?: Period,
    options?: { resetManualMatches?: boolean }
  ): Promise<FinanceDropiBackfillResponse> {
    const response = await axiosInstanceWebsite.post<FinanceDropiBackfillResponse>(
      "/finance/dropi/backfills",
      options?.resetManualMatches ? { reset_manual_matches: true } : {},
      period ? { params: buildPeriodParams(period) } : undefined
    );
    return response.data;
  },
  async listMetaCampaigns(adAccountId?: string): Promise<FinanceCampaignRow[]> {
    if (!adAccountId) return [];

    try {
      const response = await axiosInstanceWebsite.get(`/meta/campaigns/${adAccountId}/ads`, {
        params: { date_preset: "last_30d" },
      });
      const ads = response.data?.ads;
      if (!Array.isArray(ads)) return [];
      return (ads as MetaAdRow[]).map((row) => {
        const spend = Number(row.spend ?? 0);
        const purchases = Number(row.purchases ?? row.results ?? 0);
        const roas = Number(row.roas ?? 0);
        return {
          id: row.ad_id ?? row.ad_name ?? "meta-row",
          name: row.campaign_name ?? row.ad_name ?? "Campaña sin nombre",
          status: row.effective_status === "ACTIVE" ? "ACTIVE" : row.effective_status === "PAUSED" ? "PAUSED" : "OTHER",
          spend,
          revenue: roas > 0 ? spend * roas : 0,
          purchases,
          roas,
          cxc: Number(row.cost_per_result ?? (purchases > 0 ? spend / purchases : 0)),
          ctr: Number(row.ctr ?? 0),
          lastActivity: row.updated_time ?? "",
        };
      });
    } catch {
      return [];
    }
  },
};
